import { config } from '@iheartjane/config';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { Spacing } from '@iheartjane/reefer';
import {
  Box,
  Button,
  Flex,
  JanePayLogoWhite,
  Typography,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import QRCode from 'react-qrcode-logo';

import {
  OrderCompleteCheckIcon,
  OrderCompleteContainer,
  OrderCompleteSubContainer,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { useGetKiosk } from '@jane/kiosk/data-access';
import {
  useKioskSpacing,
  useShouldKioskShowGold,
  useShowJanePayOnKiosk,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useCartInfoForTracking, useLogoutUser } from '@jane/kiosk/providers';

import qrPng from '../assets/qr-logo.png';

export const OrderComplete = () => {
  const { kiosk: initialKiosk, kioskId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kiosk } = useGetKiosk({
    kioskId,
    initialData: initialKiosk,
  });
  const showGold = useShouldKioskShowGold();
  const showJanePay = useShowJanePayOnKiosk();
  const { cartInfo } = useCartInfoForTracking();
  const { cart, selectedBankAccount, showPayByLink, signedInOrder } =
    cartInfo ?? {
      cart: null,
      selectedBankAccount: null,
      showPayByLink: false,
      signedInOrder: null,
    };

  const hasCashBack =
    selectedBankAccount && (cart?.brand_discounts_total ?? 0) > 0;

  const { logoutAndReset } = useLogoutUser();

  const { isIpad } = useKioskSpacing();

  const queueNumber = localStorage.getItem('queueNumber');

  const qrWidth = isIpad ? 160 : 216;
  const qrUrl =
    cart?.store?.id && cart?.id
      ? `${config.marketplaceUrl}/pay/${cart.store.id}/${cart.id}?source=kiosk`
      : null;
  const showJanePayQRCode =
    useFlag(FLAGS.c3KioskPayByLink) && qrUrl && showPayByLink && showJanePay;

  return (
    <OrderCompleteContainer
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
      width="100%"
      height="100%"
    >
      <Flex mt={24} mb={isIpad ? 12 : 24}>
        <OrderCompleteCheckIcon
          width={isIpad ? 96 : 120}
          height={isIpad ? 96 : 120}
          color="text-info"
        />
      </Flex>

      <Typography
        color="text-info"
        textAlign="center"
        variant={isIpad ? 'h2-semibold' : 'h1-semibold'}
        as="h2"
        mb={isIpad ? 12 : 24}
        width="60%"
      >
        {kiosk?.checkout_confirmation_message ||
          'All set! Grab your order at the counter.'}
      </Typography>

      {queueNumber && (
        <Box
          borderRadius="xl"
          px={32}
          py={24}
          background="transparent-white-ten"
        >
          <Typography
            color="text-info"
            variant={isIpad ? 'h3-semibold' : 'h2-semibold'}
            style={{ fontSize: isIpad ? '24px' : '36px' }}
            as="h1"
          >
            Order #{queueNumber}
          </Typography>
        </Box>
      )}

      {showGold && signedInOrder && (
        <>
          <Typography
            color="text-info"
            textAlign="center"
            variant="h2-regular"
            style={{
              fontSize: isIpad ? '24px' : '36px',
              lineHeight: isIpad ? '32px' : '44px',
            }}
            mt={24}
            as="p"
          >
            You've been signed out of your account for your privacy.
          </Typography>
          {hasCashBack && (
            <OrderCompleteSubContainer
              alignItems="center"
              flexDirection="column"
              mt={48}
              p={isIpad ? 24 : 48}
              gap={isIpad ? 24 : 40}
            >
              <Typography
                textAlign="center"
                color="text-info"
                variant="h4-semibold"
                style={{
                  fontSize: isIpad ? '20px' : '24px',
                  lineHeight: isIpad ? '24px' : '30px',
                }}
                as="h2"
              >
                Your estimated Jane Gold cash back
              </Typography>
              <Typography
                textAlign="center"
                color="text-info"
                variant="h1-semibold"
                as="p"
              >
                {formatCurrency(cart?.brand_discounts_total)}
              </Typography>
              <Typography
                textAlign="center"
                color="text-info"
                variant="small-body-regular"
                style={{
                  fontSize: isIpad ? '13px' : '18px',
                  lineHeight: isIpad ? '16px' : '22px',
                }}
                as="p"
              >
                Your brand-sponsored cash back reward will be deposited to your
                linked bank account at {selectedBankAccount} in 1-3 days.
              </Typography>
            </OrderCompleteSubContainer>
          )}
        </>
      )}

      {showJanePayQRCode && (
        <Flex
          background="transparent-white-ten"
          p={isIpad ? 24 : 32}
          borderRadius="lg"
          mt={(isIpad ? 100 : 200) as Spacing}
          width="72%"
        >
          <Flex background="grays-white" p={16} borderRadius="lg">
            <QRCode
              id="mobile-jpsms-qr"
              fgColor="black"
              logoImage={qrPng}
              logoWidth={qrWidth * 0.3}
              size={qrWidth}
              value={qrUrl}
              quietZone={0}
              eyeRadius={10}
            />
          </Flex>
          <Flex flexDirection="column" justifyContent="center" ml={32}>
            <Typography
              color="text-info"
              variant="h2-semibold"
              mb={isIpad ? 8 : 16}
              style={{
                fontSize: isIpad ? '24px' : '36px',
                lineHeight: isIpad ? '32px' : '44px',
              }}
            >
              Pay by bank while you wait
            </Typography>
            <Typography
              color="text-info"
              variant="h4-regular"
              style={{
                fontSize: isIpad ? '24px' : '28px',
                lineHeight: isIpad ? '32px' : '36px',
              }}
              mb={isIpad ? 8 : 16}
            >
              Fast and secure payment from your phone.
            </Typography>
            <Flex alignItems="center">
              <Typography
                color="text-info"
                style={{
                  fontSize: '22px',
                  lineHeight: '28px',
                }}
                mr={8}
              >
                Powered by
              </Typography>
              <JanePayLogoWhite
                style={{ marginTop: 3 }}
                width={98}
                height={24}
              />
            </Flex>
          </Flex>
        </Flex>
      )}

      <Button
        label={
          <Typography
            color="text-inverse"
            variant={isIpad ? 'body-semibold' : 'h2-semibold'}
          >
            Done
          </Typography>
        }
        mt={isIpad ? 48 : 64}
        mb={24}
        full={isIpad ? true : false}
        size={isIpad ? 'default' : 'jumbo'}
        onClick={() => logoutAndReset()}
        variant="secondary"
        style={{ width: isIpad ? '25%' : 'auto' }}
      />
    </OrderCompleteContainer>
  );
};
